import React from 'react';
import {PracticeFeature} from '@practicegenius/domain/src/data/practice';
import {buildIdLogoutUrlFromCurrentUrl} from '@practicegenius/next-utils/src/id-dot/sharedRoutes';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import {useTickerAction} from '@practicegenius/domain/src/hooks/useTickerAction';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import {replaceSubdomainInUrl} from '@practicegenius/pg-util/src/UrlUtils';
import {useAppDebugInfo} from './useAppDebugInfo';
import {inMobileApp, postMessageToMobileApp} from '../utils/MobileApp';

type PracticeInfo = {
  id: number;
  hubUrl: string;
  practiceFeatures: PracticeFeature[];
};

type Props = {
  practiceInfo?: PracticeInfo;
  authenticated?: true;
  inNewApp?: true;
};

const useSwapAppMenuItem = ({
  practiceInfo: practice,
  inNewApp,
}: Props): JSX.Element | null => {
  if (
    !practice ||
    !practice.practiceFeatures.includes('try_members_app') ||
    !practice.practiceFeatures.includes('members_app_beta')
  )
    return null;

  const itemHref = (() => {
    if (inNewApp) return practice.hubUrl;

    return replaceSubdomainInUrl(practice.hubUrl, 'members').toString();
  })();

  return (
    <ListItem disableGutters>
      <ListItemButton component="a" href={itemHref} data-external={!inNewApp}>
        Swap to {inNewApp ? 'Old' : 'New'} App
      </ListItemButton>
    </ListItem>
  );
};

const useDebugMenuItem = (): JSX.Element | null => {
  const debugInfo = useAppDebugInfo();
  return !debugInfo?.isDebug ? null : (
    <ListItem disableGutters>
      <ListItemButton
        onClick={() => {
          postMessageToMobileApp({
            type: 'open_mobile_debug',
          });
        }}
      >
        Debug Menu
      </ListItemButton>
    </ListItem>
  );
};

const useLogoutMenuItem = ({inNewApp}: Props) => {
  if (!inNewApp) return null;

  return (
    <ListItem disableGutters>
      <ListItemButton
        component="a"
        href={buildIdLogoutUrlFromCurrentUrl(window.location.origin, {
          loginPerspective: 'member',
        })}
        data-external
      >
        Logout
      </ListItemButton>
    </ListItem>
  );
};

// TODO extract to pg-ui
export const useSecretMobileUtilityMenu = (props: Props) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  // These are hooks so they can give us a legitimate null value if they shouldn't render.
  //  This is important for providing the correct value of `displayUtilityMenu`
  const swapAppMenuItem = useSwapAppMenuItem(props);
  const debugMenuItem = useDebugMenuItem();
  const logoutMenuItem = useLogoutMenuItem(props);

  // Display the menu if we're in the mobile app and there's at least one item to display
  const displayMenu =
    inMobileApp &&
    [swapAppMenuItem, debugMenuItem, logoutMenuItem].filter(Boolean).length > 0;

  const ticker = useTickerAction({
    numTicks: 3,
    onTickCountReached: () => {
      setDialogOpen(true);
    },
  });

  const dialog = !displayMenu ? null : (
    <Dialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      onClick={() => setDialogOpen(false)}
    >
      <DialogTitle>Mobile App Utilities</DialogTitle>
      <List>
        {swapAppMenuItem}
        {debugMenuItem}
        {logoutMenuItem}
      </List>
    </Dialog>
  );
  return {
    displayMenu,
    dialog,
    incrementTickCount: () => {
      if (displayMenu) ticker.incrementTickCount();
    },
  };
};
